import BackendService from 'BackendService';
import FundOpportunityChatResponse from 'FundOpportunityChatResponse';
import { useCallback, useEffect, useRef, useState } from 'react';
import AIChatMessage from '../../../models/AIChatMessage';
import { AIChatFundOpportunityCreatorProps, CurrentData } from './types';
import { useFundOpportunityDocumentUpload } from './useFundOpportunityDocumentUpload';
import { useFundOpportunityJobPolling } from './useFundOpportunityJobPolling';
import {
  appendChatResponseMessage,
  loadUploadedDocuments,
  removeAddressesAtIndices,
  summarizeValidationIssues,
} from './utils';

type ValidationIssue = { path?: Array<string | number>; message?: string };
type ValidationError = Error & { details?: ValidationIssue[] };

export function useAIChatFundOpportunityCreator({ onSuccess }: AIChatFundOpportunityCreatorProps) {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [currentData, setCurrentData] = useState<CurrentData>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [fieldBeingAskedFor, setFieldBeingAskedFor] = useState<
    keyof NonNullable<CurrentData> | null | undefined
  >();
  const [missingRequiredFields, setMissingRequiredFields] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const currentDataRef = useRef(currentData);
  const messagesRef = useRef(messages);
  const fieldBeingAskedForRef = useRef(fieldBeingAskedFor);
  const isCreatingRef = useRef(false);

  useEffect(() => {
    currentDataRef.current = currentData;
  }, [currentData]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    fieldBeingAskedForRef.current = fieldBeingAskedFor;
  }, [fieldBeingAskedFor]);

  const documentUpload = useFundOpportunityDocumentUpload({
    setMessages,
    setCurrentData,
    messagesRef,
    currentDataRef,
  });

  const { pollJobProgress } = useFundOpportunityJobPolling({
    setMessages,
    setCurrentData,
    setFieldBeingAskedFor,
    setIsProcessing,
    setHasUnprocessedDocuments: documentUpload.setHasUnprocessedDocuments,
    setMissingRequiredFields,
  });

  const handleSendMessage = useCallback((input?: string): Promise<void> => {
    return new Promise((resolve) => {
      const submitTime = new Date();
      const newMessages = [...messagesRef.current];

      if (input) {
        newMessages.push({
          role: 'user',
          parts: [{ text: input }],
          timestamp: submitTime,
        });
      }

      setIsProcessing(true);
      setMessages(newMessages);

      const tempFundOpportunityId = currentDataRef.current?.tempFundOpportunityId;
      const uploadedDocs = tempFundOpportunityId
        ? loadUploadedDocuments(tempFundOpportunityId)
        : [];

      new BackendService()
        .generateFundOpportunity({
          inputData: input,
          currentData: currentDataRef.current,
          history: messagesRef.current.length > 0 ? messagesRef.current : undefined,
          fieldBeingAskedFor: (fieldBeingAskedForRef.current ?? null) as
            | keyof FundOpportunityChatResponse['currentData']
            | null
            | undefined,
          uploadedDocuments: uploadedDocs.length > 0 ? uploadedDocs : undefined,
        })
        .then((result: FundOpportunityChatResponse) => {
          const { currentData: nextCurrentData, furtherRequestForDetail } = result;
          const updatedMessages = appendChatResponseMessage(newMessages, result);

          setMessages(updatedMessages);
          setCurrentData(nextCurrentData);
          setFieldBeingAskedFor(furtherRequestForDetail?.fieldBeingAskedFor || null);
          setMissingRequiredFields(result.missingRequiredFields || []);
          setIsProcessing(false);
          resolve();
        });
    });
  }, []);

  useEffect(() => {
    handleSendMessage();
  }, [handleSendMessage]);

  const handleProcessDocuments = useCallback(async (): Promise<void> => {
    if (documentUpload.uploadedDocuments.length === 0) {
      return;
    }

    const tempFundOpportunityId = documentUpload.getOrCreateTempFundOpportunityId();

    const processingMessage: AIChatMessage = {
      role: 'model',
      parts: [
        {
          text: `🔄 Processing ${documentUpload.uploadedDocuments.length} document(s)... This may take a moment.`,
        },
      ],
      timestamp: new Date(),
    };

    setMessages((previousMessages) => [...previousMessages, processingMessage]);
    setIsProcessing(true);

    try {
      const backendService = new BackendService();
      const jobResponse = await backendService.processUploadedDocuments(
        tempFundOpportunityId,
        currentDataRef.current,
        messagesRef.current,
      );

      pollJobProgress(jobResponse.jobId, backendService);
    } catch (error) {
      console.error('Error starting document processing:', error);

      setMessages((previousMessages) => [
        ...previousMessages,
        {
          parts: [
            {
              text: 'Sorry, Vera had trouble starting document processing. Please try again.',
            },
          ],
          role: 'model',
          timestamp: new Date(),
        },
      ]);
      setIsProcessing(false);
    }
  }, [documentUpload, pollJobProgress]);

  const handleComplete = useCallback(() => {
    if (isCreatingRef.current) {
      return;
    }

    isCreatingRef.current = true;
    setIsCreating(true);
    setIsProcessing(true);

    new BackendService()
      .createCompleteFundOpportunity(currentDataRef.current)
      .then((id) => {
        onSuccess(`/opportunities/${id}`);
      })
      .catch((error: ValidationError) => {
        console.error('Error creating fund opportunity:', error);

        const details = error?.details || [];
        const failedFields = details
          .map((issue) => issue.path?.[0])
          .filter((field): field is string => typeof field === 'string');
        const issuesSummary = summarizeValidationIssues(details);

        const canned: AIChatMessage = {
          role: 'model',
          parts: [
            {
              text: issuesSummary
                ? `I couldn't create the fund opportunity yet — there's an issue with: ${issuesSummary}. Let's fix ${failedFields.length > 1 ? 'these' : 'that'} — what's the correct value?`
                : "I couldn't create the fund opportunity yet — some information still needs fixing. Please review the fields below or tell me what to correct.",
            },
          ],
          timestamp: new Date(),
        };
        setMessages((previousMessages) => [...previousMessages, canned]);

        if (failedFields.length > 0) {
          setCurrentData((previousData) => {
            if (!previousData) return previousData;
            const updatedData = { ...previousData };
            failedFields.forEach((fieldName) => {
              const existing = updatedData[fieldName as keyof typeof updatedData] as any;
              if (existing && typeof existing === 'object' && 'value' in existing) {
                updatedData[fieldName as keyof typeof updatedData] = {
                  ...existing,
                  value: null,
                } as never;
              }
            });
            return updatedData;
          });
          setMissingRequiredFields((previous) =>
            Array.from(new Set([...previous, ...failedFields])),
          );
          setFieldBeingAskedFor(failedFields[0] as keyof NonNullable<CurrentData>);
        }
      })
      .finally(() => {
        isCreatingRef.current = false;
        setIsCreating(false);
        setIsProcessing(false);
      });
  }, [onSuccess]);

  const handleSetField = useCallback((fieldName: string, value: string | number | string[]) => {
    setCurrentData(
      (previousData) =>
        ({
          ...(previousData || {}),
          [fieldName]: {
            value,
            attempts: 0,
            reasoning: { file: 'user', page: 0, quote: 'Manually entered by admin' },
          },
        }) as CurrentData,
    );
    setMissingRequiredFields((previous) => previous.filter((field) => field !== fieldName));
  }, []);

  const handleRemoveField = useCallback((fieldName: string) => {
    setCurrentData((previousData) => {
      if (!previousData) {
        return previousData;
      }

      const updatedData = { ...previousData };
      delete updatedData[fieldName as keyof typeof updatedData];
      return updatedData;
    });
  }, []);

  // Lets the populated-fields panel be edited directly, without going
  // through the chat — keeps whatever reasoning/attempts metadata the field
  // already had, it's just the value that changes.
  const handleEditField = useCallback((fieldName: string, newValue: string | number) => {
    setCurrentData((previousData) => {
      if (!previousData) {
        return previousData;
      }

      const existingField = previousData[fieldName as keyof typeof previousData] as
        | { value: unknown; attempts?: number; reasoning?: unknown }
        | undefined;

      return {
        ...previousData,
        [fieldName]: {
          ...existingField,
          value: newValue,
        },
      };
    });
  }, []);

  const handleRemoveAddress = useCallback((addressIndex: number) => {
    setCurrentData((previousData) => {
      if (!previousData?.addresses) {
        return previousData;
      }

      const updatedAddresses = [...previousData.addresses];
      updatedAddresses.splice(addressIndex, 1);

      return {
        ...previousData,
        addresses: updatedAddresses.length > 0 ? updatedAddresses : undefined,
      };
    });
  }, []);

  const handleRemoveAddresses = useCallback((addressIndices: number[]) => {
    setCurrentData((previousData) => {
      if (!previousData?.addresses) {
        return previousData;
      }

      const updatedAddresses = removeAddressesAtIndices(previousData.addresses, addressIndices);

      return {
        ...previousData,
        addresses: updatedAddresses.length > 0 ? updatedAddresses : undefined,
      };
    });
  }, []);

  return {
    messages,
    currentData,
    isProcessing,
    isCreating,
    missingRequiredFields,
    isUploadingFile: documentUpload.isUploadingFile,
    uploadProgress: documentUpload.uploadProgress,
    uploadedDocuments: documentUpload.uploadedDocuments,
    handleSendMessage,
    handleDocumentUpload: documentUpload.handleDocumentUpload,
    handleProcessDocuments,
    handleRemoveDocument: documentUpload.handleRemoveDocument,
    handleComplete,
    handleEditField,
    handleRemoveField,
    handleRemoveAddress,
    handleRemoveAddresses,
    handleSetField,
  };
}
